import { FC, Fragment, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { TFunction } from "../../i18n/tt";
import { Button, LinkButton } from "../../components-v2/Button";
import Shareholders from "./Shareholders";
import { Alert } from "../../components-v2/Alert";
import { OnboardingStatusService } from "../../onboardingstatus/OnboardingStatusApi";
import { OnboardingStatusController } from "../../onboardingstatus/OnboardingStatusController";
import { isOk, isPending } from "../../common/common";
import { DefaultLayout } from "../../containers";
import RunAutoimportDialog from "./RunAutoimport";
import { Checkbox } from "../../components-v2/Checkbox";
import { Dialog } from "../../components-v2/Dialog";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import { MTextInput } from "../../components-v2/TextInput";
import { ShareholderOnboardingStatus } from "./ShareholderOnboardingStatus";
import { MSelect, Select } from "../../components-v2/Select";
import { Box, Grid, GridItem, VBox } from "../../components-v2/Box";
import { HiddenForMobile } from "../../components-v2/Mobile";
import { isDesktopScreen, isMobileView } from "../../utils/screen";
import {
  useLanguage,
  useRootStore,
  useShareholdersStore,
  useTranslation,
  useUserStore,
} from "../../stores/RootStoreContext";
import { navigateToEditShareholders, navigateToShareholdersImport } from "../../utils/navigation";
import { EmptyState } from "../../components-v2/EmptyState";
import Laptop from "../../assets/img/dcompany_laptop_front.svg";
import { EmptyShareholderStateEvents } from "./shareholdersDomain";
import {
  NotifyShareholderEmailModel,
  NotifyShareholdersStore,
} from "../../stores/NotifyShareholdersStore";
import { OptionFieldModel } from "../../common/commonForms";
import { prepShareholderStatusFilter } from "../shareholderRegisterDomain";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { Language } from "../../i18n/languages";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";
import { compact } from "lodash";
import { Tag } from "../../components-v2/Tag";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { Markdown } from "../../text/Markdown";

import { ShareholderFollowUpReviewDialog } from "../../portfolio/followUp/ShareholderFollowUpReviewDialog";

function infoBoxMissingOwner(t: TFunction) {
  return (
    <Box>
      <Alert
        type="warning"
        message={
          <VBox>
            <Box>{t("shareholders.warning.missingShareholder")}</Box>
            <Box>
              <ul>
                <li>{t("shareholders.warning.reasonShareholderUnderAge")}</li>
                <li>{t("shareholders.warning.reasonCompanyDissolved")}</li>
                <li>{t("shareholders.warning.reasonDead")}</li>
              </ul>
            </Box>
          </VBox>
        }
      />
    </Box>
  );
}

export const ShareholdersPage = observer(() => {
  const { userStore, http: api, history } = useRootStore();
  const { t } = useTranslation();
  const { getCompanyInfo, hasShareholderRegisterSubscription } = useUserStore();
  const companyInfo = getCompanyInfo();
  const controller = useShareholdersStore();
  const {
    activeShareholders,
    descriptionMessage,
    hasInvalidShareholders,
    hasMissingShareholders,
    hasShareholders,
    invalidShareholders,
    isLoading,
    loadAllShareholders,
    shareholdersResult,
    showEmptyStateForFilteredShareholders,
  } = controller;
  const locale = useLanguage();
  const { orgNumber, userRole, accessType } = userStore;
  const onboardingStatusController = useMemo(
    () => new OnboardingStatusController(new OnboardingStatusService(api), orgNumber, accessType),
    [api, orgNumber, accessType],
  );
  const canEdit = userRole?.allowShareholdersEdit ?? true;
  const renderOnboardingStatus = !hasShareholderRegisterSubscription && canEdit;
  const { pathname: baseUrl, search: queryString } = useRootStore().location;
  const currentUrl = baseUrl + queryString;

  useEffect(() => {
    onboardingStatusController.loadOnboardingStatus();
  }, [onboardingStatusController]);

  useEffect(() => {
    if (companyInfo) {
      loadAllShareholders();
    }
  }, [companyInfo, loadAllShareholders]);

  useEffect(() => {
    if (isOk(controller.shareholdersResult) && isOk(onboardingStatusController.onboardingSteps)) {
      controller.validateActiveShareholders(
        onboardingStatusController.markShareholdersIncompleteIfValidationErrors,
      );
    }
  }, [
    controller,
    controller.shareholdersResult,
    onboardingStatusController,
    onboardingStatusController.onboardingSteps,
  ]);

  return (
    <DefaultLayout container={"2xl"}>
      {isPending(shareholdersResult) ? (
        <LoadingIndicator fullscreen />
      ) : !hasShareholders ? (
        <EmptyShareholderState
          events={{
            onImportShareholders: controller.autoimport,
            onAddShareholdersManually: () => navigateToEditShareholders(orgNumber, history),
            onImportFromFile: () => navigateToShareholdersImport(orgNumber, history),
            onAddShareholdersFromPortfolio: () =>
              controller.openActorSelectorDialog({
                title: { key: "shareholders.add-shareholders" },
                excludeActors: controller.excludeAllShareholdersAsActors,
                onSubmit: (selectedActors, onSuccess, onError) => {
                  controller.addShareholdersFromActors(
                    selectedActors,
                    () => {
                      controller.loadAllShareholders();
                      onSuccess();
                    },
                    onError,
                  );
                },
              }),
          }}
        />
      ) : (
        <VBox>
          {controller.followUps.length > 0 && (
            <Alert
              type={"info"}
              icon={"info"}
              paddingY={"sm"}
              message={
                <Box>
                  <Markdown
                    value={t("shareholders.companyname-followup.alert", {
                      count: controller.followUps.length,
                    })}
                  />
                  <LinkButton
                    label={t("shareholders.companyname-followup.review")}
                    onClick={controller.openFollowUpDialog}
                  />
                </Box>
              }
            />
          )}
          <Box justifyContent={"space-between"} vertical={isMobileView()} wrap>
            <Box vertical={isMobileView()} alignItems={isMobileView() ? undefined : "center"}>
              <Select
                {...prepShareholderStatusFilter(
                  controller.shareholderStatusFilter,
                  controller.setShareholderStatusFilter,
                )}
              />
              <MSelect model={controller.shareTypeFilter} />
              <MTextInput
                model={controller.nameFilter}
                name={"shareholder_filter"}
                placeholder={t("shareholders.filter.search")}
                icon={"magnifying-glass"}
                type={"search"}
              />
            </Box>
            {canEdit && (
              <Box vertical={isMobileView()} wrap={!isMobileView()}>
                <DropdownMenu
                  button={{
                    type: "button",
                    variant: "secondary",
                    icon: "plus",
                    label: { key: "shareholders.add-shareholders" },
                  }}
                  items={compact([
                    {
                      type: "item",
                      icon: "user-plus",
                      label: { key: "shareholders.add.new" },
                      onSelect: () =>
                        controller.openEditShareholderDialog({
                          locale,
                          onSuccess: (shareholder?: ExtendedShareholderDTO) => {
                            controller.loadAllShareholders();
                            controller.setShareholderStatusFilter("all");
                            if (shareholder !== undefined) {
                              userStore.navigateToShareholderDetail(
                                shareholder.id,
                                currentUrl,
                                queryString,
                                "details",
                              );
                            }
                          },
                        }),
                    },
                    {
                      type: "item",
                      icon: "suitcaseSimple",
                      label: (
                        <Box>
                          {t("shareholders.add.from-portfolio")}
                          <Tag label={{ key: "new" }} color={"info"} />
                        </Box>
                      ),
                      onSelect: () =>
                        controller.openActorSelectorDialog({
                          title: { key: "shareholders.add-shareholders" },
                          excludeActors: controller.excludeAllShareholdersAsActors,
                          onSubmit: (selectedActors, onSuccess, onError) => {
                            controller.addShareholdersFromActors(
                              selectedActors,
                              () => {
                                controller.loadAllShareholders();
                                controller.setShareholderStatusFilter("all");
                                onSuccess();
                              },
                              onError,
                            );
                          },
                        }),
                    },
                    isDesktopScreen() && {
                      type: "item",
                      icon: "microsoft-excel-logo",
                      label: { key: "shareholders.importCSV" },
                      onSelect: () => navigateToShareholdersImport(orgNumber, history),
                    },
                  ])}
                />
                <Button
                  icon={"envelope"}
                  label={t("shareholders.notify.all")}
                  variant={"secondary"}
                  disabled={isLoading}
                  onClick={() => controller.setShowNotifyShareholdersDialog(true)}
                />
                <HiddenForMobile>
                  <Button
                    label={t("shareholders.export")}
                    variant={"secondary"}
                    disabled={isLoading}
                    onClick={controller.openExportShareholdersDialog}
                    icon={"download"}
                  />
                </HiddenForMobile>
                <Button
                  variant={"primary"}
                  icon={"grid"}
                  label={t("shareholders.editAll")}
                  onClick={() => navigateToEditShareholders(orgNumber, history)}
                />
              </Box>
            )}
          </Box>
        </VBox>
      )}
      {hasInvalidShareholders && (
        <VBox marginTop={"md"}>
          <Alert
            type="error"
            message={
              <>
                {t("invalidShareholders1")}
                <LinkButton
                  label={
                    controller.invalidShareholders !== 1
                      ? t("invalidShareholders2", { number: invalidShareholders })
                      : t("invalidShareholders3")
                  }
                  color={"inherit"}
                  onClick={() => navigateToEditShareholders(orgNumber, history, true)}
                />
              </>
            }
          />
        </VBox>
      )}
      {renderOnboardingStatus && (
        <ShareholderOnboardingStatus
          controller={onboardingStatusController}
          activeShareholders={activeShareholders}
          hasInvalidShareholders={hasInvalidShareholders}
        />
      )}
      {hasMissingShareholders && canEdit && infoBoxMissingOwner(t)}
      {hasShareholders && <Shareholders controller={controller} />}
      {showEmptyStateForFilteredShareholders && (
        <EmptyStateFilterShareholderStatus
          description={descriptionMessage(t)}
          setShareholderStatusFilter={() => controller.setShareholderStatusFilter("all")}
        />
      )}
      {!hasShareholders && controller.isRunAutoimport && (
        <RunAutoimportDialog controller={controller} />
      )}
      {controller.showNotifyShareholdersDialog && controller.notifyShareholdersStore && (
        <InviteShareholdersDialog
          controller={controller.notifyShareholdersStore}
          onClose={() => controller.setShowNotifyShareholdersDialog(false)}
        />
      )}
      {controller.followUpDialogModel && (
        <ShareholderFollowUpReviewDialog
          model={controller.followUpDialogModel}
          onSubmit={controller.submitFollowUpDialog}
          onCancel={controller.closeFollowUpDialog}
          isSubmitting={false}
        />
      )}
    </DefaultLayout>
  );
});

export const EmptyShareholderState: FC<{
  events: EmptyShareholderStateEvents;
}> = observer(({ events }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={<img src={Laptop} alt="dCompany" />}
      title={t("emptyShareholder.header")}
      description={t("emptyShareholder.info")}
      actions={[
        <Button
          variant={"primary"}
          key={"import-from-file"}
          onClick={events.onImportFromFile}
          icon={"excel"}
          label={t("emptyShareholder.importFromFileLabel")}
        />,
        <Button
          key={"import-shareholders"}
          onClick={events.onImportShareholders}
          icon={"lightning-a"}
          label={t("emptyShareholder.importShareholdersLabel")}
          variant={"secondary"}
        />,
        <Button
          key={"add-manually"}
          onClick={events.onAddShareholdersManually}
          icon={"grid"}
          label={t("emptyShareholder.addShareholdersManually")}
          variant={"secondary"}
        />,
        <Button
          key={"add-from-portfolio"}
          onClick={events.onAddShareholdersFromPortfolio}
          icon={"suitcaseSimple"}
          label={t("shareholders.add.from-portfolio")}
          variant={"secondary"}
        />,
      ]}
    />
  );
});

const EmptyStateFilterShareholderStatus = observer(
  ({
    description,
    setShareholderStatusFilter,
  }: {
    description: string;
    setShareholderStatusFilter: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox marginTop>
        <EmptyState
          illustration={<div />}
          description={description}
          actions={
            <Button
              variant={"secondary"}
              label={t("showAll")}
              onClick={setShareholderStatusFilter}
            />
          }
        />
      </VBox>
    );
  },
);

export const InviteShareholdersDialog = observer(function InviteShareholdersDialog({
  controller,
  onClose,
}: {
  controller: NotifyShareholdersStore;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const showShareholders = (controller.shareholders?.options?.length ?? 0) > 0;
  const en = useTranslation(Language.EN);
  const no = useTranslation(Language.NO);
  return (
    <Dialog
      title={t("shareholders.notify.all.heading")}
      submitLabel={t("send")}
      onSubmit={() => {
        controller.notifyShareholders();
        onClose();
      }}
      onCancel={onClose}
      isSubmitting={controller.isNotifyingShareholders}
      isSubmitDisabled={controller.isSubmitDisabled}
    >
      <VBox>
        {t("shareholders.notify.description")}
        <Box gap={"md"} marginTop={"lg"} marginBottom={"lg"}>
          <div>{t("shareholders.notify.email.language")}</div>
          <MRadioButtonGroup model={controller.notifyShareholdersEmailLanguage} horizontal />
        </Box>
        <VBox>
          <strong>{t("invite.user.email.title")}</strong>
          <i>
            {controller.notifyShareholdersEmailLanguage.value === Language.NO
              ? no.t(controller.generateCommonEmailBody)
              : en.t(controller.generateCommonEmailBody)}
          </i>
        </VBox>
        <Box
          justifyContent={controller.isPersonalNoteEnabled ? "space-between" : "start"}
          marginBottom={controller.isPersonalNoteEnabled ? "sm" : "lg"}
          marginTop={"lg"}
        >
          {controller.isPersonalNoteEnabled && <div>{t("invite-user.personal.note")}</div>}
          <LinkButton
            label={
              controller.isPersonalNoteEnabled ? t("remove") : t("invite-user.add.personal.note")
            }
            icon={controller.isPersonalNoteEnabled ? "trash" : "plus"}
            onClick={controller.togglePersonalNote}
          />
        </Box>
        <VBox gap={"xl"}>
          {controller.isPersonalNoteEnabled && (
            <LocalizedTextEditor model={controller.personalNote} autofocus />
          )}
          {showShareholders && (
            <ShareholderEmailCheckBoxes shareholders={controller.shareholders} />
          )}
        </VBox>
      </VBox>
    </Dialog>
  );
});

const ShareholderEmailCheckBoxes = observer(function ShareholderEmailCheckBoxes({
  shareholders,
}: {
  shareholders?: OptionFieldModel<NotifyShareholderEmailModel>;
}) {
  const { t } = useTranslation();
  return (
    <VBox>
      <Box gap={"md"}>
        <strong>{t("archive.send-for-signing.send-to")}</strong>
        <LinkButton
          label={shareholders?.isAllSelected ? t("select-none") : t("select-all")}
          onClick={() =>
            shareholders?.isAllSelected
              ? shareholders.toggleAll(false)
              : shareholders?.toggleAll(true)
          }
        />
      </Box>
      <Grid maxWidth={"sm"} alignItems={"center"}>
        {shareholders?.options.map((s) => (
          <Fragment key={s.id}>
            <GridItem xs={6}>
              <Checkbox
                checked={shareholders?.isSelected(s)}
                label={s.name}
                onChange={() => {
                  shareholders?.toggle(s);
                }}
              />
            </GridItem>
            <GridItem xs={6}>
              <MTextInput
                placeholder={t("email")}
                model={s.email}
                disabled={!shareholders?.isSelected(s)}
              />
            </GridItem>
          </Fragment>
        ))}
      </Grid>
    </VBox>
  );
});
